import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import type { SchemaProblem, ValidateSchemaResult } from '../../lib/validateSchema';
import { ReportIcon } from './ReportIcon';
import { summarizeReport } from './reportSummary';

export interface ValidateSummaryProps {
  report: ValidateSchemaResult;
  /** Findings that point at a cell or a column, so the grid can be taken to them. */
  anchoredProblems: SchemaProblem[];
  /** Findings that point at the schema as a whole, and so anchor nowhere. */
  schemaProblems: SchemaProblem[];
  tab: 'fields' | 'rules';
  /** Reveals a finding in the grid. Absent while read-only. */
  onJumpToProblem?: (problem: SchemaProblem) => void;
  /** Applies a finding's one-click repair. Absent while read-only. */
  onFix?: (problem: SchemaProblem) => void;
  /** Leaves out the icon and count line, where the caller already shows them. */
  hideHeadline?: boolean;
}

/** Errors before warnings, then in the order the validator found them. */
function bySeverity(a: SchemaProblem, b: SchemaProblem) {
  if (a.severity === b.severity) return 0;
  return a.severity === 'error' ? -1 : 1;
}

export function ValidateSummary({
  report,
  anchoredProblems,
  schemaProblems,
  tab,
  onJumpToProblem,
  onFix,
  hideHeadline = false,
}: ValidateSummaryProps) {
  const { t } = useTranslation('knowledge');
  const anchored = [...anchoredProblems].sort(bySeverity);

  return (
    <>
      {!hideHeadline && <ReportIcon report={report} className="mt-0.5" />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {!hideHeadline && (
          <span className="font-medium text-foreground">{summarizeReport(report, t)}</span>
        )}

        {/*
          Each anchored finding is a control that takes you to it, not a line of
          copy telling you to hover for it. The grid windows past 50 rows, so a
          finding below the fold had no element to hover — the instruction could
          not be followed however patiently you tried.
        */}
        {anchored.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1">
            {anchored.map((problem) => (
              <li key={problem.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {onJumpToProblem ? (
                  <button
                    type="button"
                    onClick={() => onJumpToProblem(problem)}
                    className="text-left underline decoration-dotted underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="text-foreground">{problem.message}</span>
                    {problem.hint ? ` ${problem.hint}` : ''}
                  </button>
                ) : (
                  <span className="text-muted-foreground">
                    <span className="text-foreground">{problem.message}</span>
                    {problem.hint ? ` ${problem.hint}` : ''}
                  </span>
                )}
                {problem.fix && onFix && (
                  <Button variant="outline" size="mini" onClick={() => onFix(problem)}>
                    {t('taxonomy.taxonomyDetail.useDisplayValuesAsCodes')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/*
          Kept apart from the list above, and deliberately not clickable: these
          describe the schema as a whole and anchor to nothing, so offering a
          jump would promise a place to land that does not exist.
        */}
        {schemaProblems.length > 0 && (
          <>
            {anchored.length > 0 && (
              <span className="mt-1 text-muted-foreground">
                {t('taxonomy.taxonomyDetail.notTiedToAColumn')}
              </span>
            )}
            <ul className="flex flex-col gap-1">
              {schemaProblems.map((problem) => (
                <li key={problem.id} className="text-muted-foreground">
                  <span className="text-foreground">{problem.message}</span>
                  {problem.hint ? ` ${problem.hint}` : ''}
                </li>
              ))}
            </ul>
          </>
        )}

        {/* The fields tab is where a jump can land; from the rules tab it switches first. */}
        {anchored.length > 0 && tab === 'rules' && (
          <span className="text-muted-foreground">
            {t('taxonomy.taxonomyDetail.markedOnFieldsTab', { count: anchored.length })}
          </span>
        )}
      </div>
    </>
  );
}
